import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { endpoint } from "../../api/endpoint";
import {
  saveAuthData,
  saveModule,
  saveRole,
  saveAuthPermission,
  saveUser,
} from "../../features/login/LoginSlice";
import { apiHandler } from "../../api";
import Joi from "joi";
import { RegistrationForm } from "../../containers/RegistrationForm/RegistrationForm";
import { useNavigate } from "react-router";

// import { useNavigate } from "react-router";

export const useAuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [appName, setAppName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [hidePage, setHidePage] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [loader, setLoader] = useState(false);
  const [apiKeyValid, setIsApiKeyValid] = useState(true);
  const [raiseIssue, setRaiseIssue] = useState("");
  const [attribute, setAttribute] = useState("");
  const [tenantId, settenantId] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrlParam = queryParams.get("redirectUrl");
    const appNameParam = queryParams.get("appName");
    const apiKeyParam = queryParams.get("apiKey");
    const sApplication = queryParams.get("sApplication");
    const raiseIssue = queryParams.get("raiseIssue");
    const attribute = queryParams.get("attribute");
    const tenantId = queryParams.get("tenant_id");
    settenantId(tenantId);
    setRaiseIssue(raiseIssue);
    setAttribute(attribute);
    if (redirectUrlParam) {
      setRedirectUrl(redirectUrlParam);
    }
    if (appNameParam) {
      setAppName(appNameParam);
    }
    if (apiKeyParam) {
      setApiKey(apiKeyParam);
      handleApiKeyRedirection(apiKeyParam, sApplication, raiseIssue, attribute);
    }
  }, []);

  const handleApiKeyRedirection = async (apiKey, sApplication) => {
    try {
      setLoader(true);

      const response = await apiHandler({
        url: endpoint.API_KEY_REDIRECTION,
        method: "POST",
        data: { apiKey, application: sApplication },
      });

      if (response.data.statusCode === 201) {
        setIsApiKeyValid(false);

        const appRedirectMap = {
          "Seller App": "https://futuresoftmsn.clouddeploy.in/",
          "IGM Version 2": "https://igmfront.clouddeploy.in/",
          RSF: "https://futuresoftondcrsf.preprod.clouddeploy.in/",
        };

        const baseUrl = appRedirectMap[sApplication] || "";
        if (!baseUrl) {
          addToast("Invalid application name.", {
            appearance: "error",
            autoDismiss: true,
          });
          setLoader(false);
          return;
        }

        const redirectUrl =
          `${baseUrl}login` +
          `?auth=${encodeURIComponent(
            JSON.stringify({
              accessToken: response.data.data.auth.accessToken,
              refreshToken: response.data.data.auth.refreshToken,
            })
          )}` +
          `&navbar=${encodeURIComponent(
            JSON.stringify(response?.data?.data?.navbar)
          )}` +
          `&authorization=${encodeURIComponent(
            JSON.stringify(response.data.data?.userDetails.authorization)
          )}` +
          `&apiKey=${response.data.data?.userDetails.apiKey || ""}` +
          `&isAdmin=${encodeURIComponent(
            response.data.data?.userDetails.isAdmin || false
          )}` +
          `&raiseIssue=${encodeURIComponent(raiseIssue || "")}` +
          `&attribute=${encodeURIComponent(attribute || "")}` +
          `&brandName=${encodeURIComponent(
            response.data.data?.userDetails?.tenantDetails?.brandName || ""
          )}`;

        window.location.href = redirectUrl;
        return;
      }

      if (response.data.statusCode === 500) {
        setIsApiKeyValid(true);
        setLoader(false);
        return;
      }
      if (response.data.statusCode === 409) {
        setShowRegistration(true);
        setIsApiKeyValid(false);
        setLoader(false);
        return;
      }

      setIsApiKeyValid(false);
      addToast(response.data.message || "Something went wrong.", {
        appearance: "error",
        autoDismiss: true,
      });
      setLoader(false);
    } catch (error) {
      addToast("Error processing API key. Please try again.", {
        appearance: "error",
        autoDismiss: true,
      });
      setLoader(false);
    }
  };

  const schema = Joi.object({
    username: Joi.string()
      .email({ tlds: { allow: ["com", "net", "org", "edu", "gov"] } })
      .required()
      .messages({
        "string.email": "Please enter a valid email address.",
        "string.email.tlds":
          "The domain must be one of the following: .com, .net, .org, .edu, .gov.",
        "string.empty": "Email is required.",
      }),
    password: Joi.string().min(8).required().messages({
      "string.min": "Password must be at least 8 characters long.",
      "string.pattern.base":
        "Password must contain at least one uppercase letter and one special character.",
      "string.empty": "Password is required.",
    }),
  });

  const validate = () => {
    const { error } = schema.validate(
      { username, password },
      { abortEarly: false }
    );

    if (error) {
      const errorMessages = {};
      error.details.forEach((item) => {
        errorMessages[item.path[0]] = item.message;
      });
      setErrors(errorMessages);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validate()) {
      setLoading(true);
      try {
        const response = await apiHandler({
          url: endpoint.LOGIN,
          method: "POST",
          data: {
            sEmail: username,
            sPassword: password,
            sApplication: appName ? appName : "SaaS Configurator",
          },
          dispatch,
        });

        if (response?.data?.success) {
          if (redirectUrl) {
            window.location.href =
              redirectUrl +
              `?auth=${encodeURIComponent(
                JSON.stringify(response?.data?.data?.auth)
              )}&navbar=${encodeURIComponent(
                JSON.stringify(response?.data?.data?.navbar)
              )}&authorization=${encodeURIComponent(
                JSON.stringify(response.data.data?.userDetails.authorization)
              )}&apiKey=${encodeURIComponent(
                response.data.data?.userDetails.apiKey || ""
              )}&isAdmin=${encodeURIComponent(
                response.data.data?.userDetails.isAdmin || false
              )}&raiseIssue=${encodeURIComponent(
                raiseIssue || ""
              )}&attribute=${encodeURIComponent(
                attribute || ""
              )}&tenantId=${encodeURIComponent(tenantId || "")}&brandName=${encodeURIComponent(
                response.data.data?.userDetails?.tenantDetails?.brandName || ""
              )}`;

            return;
          }

          dispatch(
            saveAuthData({
              accessToken: response?.data?.data?.auth.accessToken,
              refreshToken: response?.data?.data?.auth.refreshToken,
            })
          );
          sessionStorage.setItem(
            "accessToken",
            response?.data?.data?.auth.accessToken
          );
          sessionStorage.setItem(
            "refreshToken",
            response?.data?.data?.auth.refreshToken
          );
          dispatch(saveRole("Admin"));
          dispatch(saveModule(response?.data?.data?.navbar));
          dispatch(
            saveAuthPermission(response.data.data?.userDetails.authorization)
          );
          dispatch(saveUser(response.data.data.userDetails));
          navigate("/");
        } else {
          addToast(response.data.message, {
            appearance: "error",
            autoDismiss: true,
          });
        }
      } catch (error) {

        addToast("An error occurred while logging in. Please try again.", {
          appearance: "error",
          autoDismiss: true,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    username,
    password,
    setUsername,
    setPassword,
    handleSubmit,
    errors,
    loading,
    showRegistration,
    apiKey,
    loader,
    apiKeyValid,
  };
};
